import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Skeleton from "@mui/material/Skeleton";
import { toast } from "react-toastify";

import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CancelIcon from "@mui/icons-material/Cancel";

import Constant from "../../Constant";
import getServices from "../../Services/get-service";

import ActiveSalesDetailsTab from "./ActiveSalesDetailsTab";
import ActiveInvestmentTransactionsTab from "./ActiveInvestmentTransactionsTab";
import ActiveInvestmentUpdatesTab from "./ActiveInvestmentUpdatesTab";

import RequestEarlyExitModal from "./RequestEarlyExitModal";
import EarlyExitPinModal from "./EarlyExitPinModal";
import PositionListedSuccessModal from "./PositionListedSuccessModal";

// Format currency
const formatCurrency = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value))
  ) {
    return "N/A";
  }

  return `₦${Constant.FormatNumber(Number(value))}`;
};

function ActiveSalesInvestmentDrawer({
  open,
  handleClose,
  property,
  onBuyClick,
}) {
  // Tabs
  const [activeTab, setActiveTab] = useState("details");

  // Investment details
  const [investmentData, setInvestmentData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState(false);

  // Updates
  const [updates, setUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  const [updatesError, setUpdatesError] = useState(false);

  // Modals
  const [activeModal, setActiveModal] = useState(null);

  // Selected property ID
  const propertyId = property?.propertyId ?? property?.id;

  // Reset tabs and modals when drawer opens or property changes
  useEffect(() => {
    setActiveTab("details");
    setActiveModal(null);
  }, [open, propertyId]);

  // Fetch active sales investment details
  useEffect(() => {
    if (!open || propertyId == null) {
      setInvestmentData(null);
      setLoadingDetails(false);
      setDetailsError("");
      return;
    }

    let cancelled = false;

    const fetchInvestmentDetails = async () => {
      setLoadingDetails(true);
      setDetailsError("");
      setInvestmentData(null);

      try {
        const response = await getServices.getActiveInvestment(propertyId);
        const { statusCode, description, data } = response?.data ?? {};

        if (statusCode !== 200) {
          throw new Error(description || "Failed to fetch investment details");
        }

        if (!data) {
          throw new Error("Investment details unavailable");
        }

        if (!cancelled) {
          setInvestmentData(data);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.description ||
            error?.message ||
            "Failed to fetch investment details";
          toast.error(message);
          setDetailsError(message);
        }
      } finally {
        if (!cancelled) {
          setLoadingDetails(false);
        }
      }
    };

    fetchInvestmentDetails();

    return () => {
      cancelled = true;
    };
  }, [open, propertyId]);

  // Fetch sales transactions
  useEffect(() => {
    if (!open || activeTab !== "transactions" || propertyId == null) {
      setTransactions([]);
      setLoadingTransactions(false);
      setTransactionsError(false);
      return;
    }

    let cancelled = false;

    const fetchTransactions = async () => {
      setLoadingTransactions(true);
      setTransactionsError(false);
      setTransactions([]);

      try {
        const response =
          await getServices.getSalesTransactionHistory(propertyId);
        const { statusCode, description, data } = response?.data ?? {};

        if (statusCode !== 200) {
          throw new Error(description || "Failed to fetch sales transactions");
        }

        if (!Array.isArray(data?.content)) {
          throw new Error("Invalid transaction response");
        }

        if (!cancelled) {
          setTransactions(data.content);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.description ||
            error?.message ||
            "Failed to fetch sales transactions";

          toast.error(message);
          setTransactionsError(true);
          setTransactions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTransactions(false);
        }
      }
    };

    fetchTransactions();

    return () => {
      cancelled = true;
    };
  }, [open, activeTab, propertyId]);

  // Fetch property updates
  useEffect(() => {
    if (!open || activeTab !== "updates" || propertyId == null) {
      setUpdates([]);
      setLoadingUpdates(false);
      setUpdatesError(false);
      return;
    }

    let cancelled = false;

    const fetchUpdates = async () => {
      setLoadingUpdates(true);
      setUpdatesError(false);
      setUpdates([]);

      try {
        const response = await getServices.getPropertyPhases(propertyId);
        const { statusCode, description, data } = response?.data ?? {};

        if (statusCode !== 200) {
          throw new Error(description || "Failed to fetch property updates");
        }

        if (!cancelled) {
          setUpdates(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.description ||
            error?.message ||
            "Failed to fetch property updates";

          toast.error(message);
          setUpdatesError(true);
          setUpdates([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingUpdates(false);
        }
      }
    };

    fetchUpdates();

    return () => {
      cancelled = true;
    };
  }, [open, activeTab, propertyId]);

  // Map API response to UI data
  const mockData = investmentData
    ? {
        propertyId: investmentData.propertyId ?? propertyId,

        title: investmentData.productName ?? "N/A",

        location: investmentData.location ?? "N/A",

        image: investmentData.propertyImages?.[0]?.imageUrl ?? null,

        type: investmentData.investmentType || "Sales",

        currentBalance: formatCurrency(investmentData.amount),

        units: String(
          investmentData.unitBought ?? investmentData.numberOfUnits ?? "N/A",
        ),

        profit: formatCurrency(investmentData.profit),

        // Sales investment details
        allocatedCostPerUnit: formatCurrency(
          investmentData.allocatedCostPerUnit,
        ),

        projectedMinValue: formatCurrency(investmentData.minValuePerUnit),

        estimatedReturnPerUnit: formatCurrency(
          investmentData.estimatedReturnPerUnit,
        ),

        constructionTenure:
          Number(investmentData.noOfDays) > 0
            ? `${investmentData.noOfDays} days`
            : "N/A",

        salesWindow:
          investmentData.salesWindowDuration != null
            ? String(investmentData.salesWindowDuration)
            : "N/A",

        timeRemaining: "N/A",

        constructionStatus: investmentData.constructionStatus ?? "N/A",

        salesWindowStatus: investmentData.salesWindowStatus ?? "N/A",

        // Early exit modal data
        originalCostPerUnit: formatCurrency(
          investmentData.allocatedCostPerUnit,
        ),

        currentValuationPerUnit: "N/A",

        estimatedPositionValue: "N/A",
      }
    : null;

  // Early exit modal actions
  const handleListOnMarket = () => {
    setActiveModal("pin");
  };

  const handlePinConfirm = () => {
    setActiveModal("success");
  };

  // Tabs configuration
  const tabs = [
    { id: "details", label: "Details" },
    { id: "transactions", label: "Transactions" },
    { id: "updates", label: "Updates" },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", md: 450 },
          boxSizing: "border-box",
          backgroundColor: "#FFFFFF",
        },
      }}
    >
      {/* Header */}
      <div className="relative h-[250px] w-full bg-gray-200 flex-shrink-0">
        {loadingDetails ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : mockData?.image ? (
          <img
            src={mockData.image}
            alt={mockData.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-sm">
            No image available
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close investment details"
          className="absolute top-4 left-4 z-10 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
        >
          <CancelIcon fontSize="small" />
        </button>

        {/* Property Information */}
        <div className="absolute bottom-4 left-4 right-4 z-10">
          {loadingDetails ? (
            <div className="space-y-2">
              <Skeleton variant="rounded" width={70} height={22} />
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={20} />
            </div>
          ) : mockData ? (
            <>
              <div className="flex gap-2 mb-2">
                <span className="bg-[#00BC67] text-white text-[12px] font-bold px-2 py-1 rounded">
                  {mockData.type}
                </span>
              </div>

              <h1
                className="text-white text-[24px] font-bold truncate"
                title={mockData.title}
              >
                {mockData.title}
              </h1>

              <p className="text-white text-[14px] flex items-center opacity-90 truncate">
                <PlaceOutlinedIcon
                  sx={{ fontSize: 16 }}
                  className="mr-1 flex-shrink-0"
                />

                {mockData.location}
              </p>
            </>
          ) : null}
        </div>
      </div>

      {/* Drawer Content */}
      <div className="p-6 overflow-y-auto">
        {loadingDetails ? (
          <div className="space-y-4 py-2">
            <div className="flex justify-between items-end">
              <div className="space-y-2">
                <Skeleton variant="text" width={100} height={16} />
                <Skeleton variant="text" width={160} height={36} />
                <Skeleton variant="text" width={120} height={16} />
              </div>

              <div className="space-y-2">
                <Skeleton variant="text" width={50} height={16} />
                <Skeleton variant="text" width={40} height={28} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
            </div>

            <Skeleton variant="rounded" height={220} />
          </div>
        ) : detailsError || !mockData ? (
          <div role="alert" className="text-center text-sm text-gray-500 py-10">
            {detailsError || "Unable to load investment details."}
          </div>
        ) : (
          <>
            {/* Investment Summary */}
            <div className="flex justify-between items-end mb-4">
              <div>
                <p className="text-[#808285] text-[12px] mb-1">
                  Investment amount
                </p>

                <p className="text-back-color2 text-[32px] font-bold leading-none tracking-tight">
                  {mockData.currentBalance}
                </p>

                {mockData.profit !== "N/A" && (
                  <p className="text-[#00BC67] text-[12px] font-medium mt-1">
                    Profit: {mockData.profit}
                  </p>
                )}
              </div>

              <div className="text-right">
                <p className="text-[#808285] text-[12px] mb-1">Units</p>

                <p className="text-back-color2 text-[18px] font-bold leading-none">
                  {mockData.units}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3 mt-6">
              <button
                type="button"
                onClick={() => onBuyClick?.(mockData)}
                className="w-full py-3 rounded-lg bg-[#00BC67] hover:bg-[#00A859] transition-colors text-white font-[600] text-[14px]"
              >
                Buy more units
              </button>

              <button
                type="button"
                onClick={() => setActiveModal("request")}
                className="w-full py-3 rounded-lg border border-[#00BC67] bg-white text-[#00BC67] hover:bg-[#00BC67] hover:text-white transition-colors font-[600] text-[14px]"
              >
                Request early exit
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100 mt-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  aria-pressed={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 text-center py-3 text-[14px] transition-colors ${
                    activeTab === tab.id
                      ? "text-back-color2 font-bold border-b-2 border-[#00BC67]"
                      : "text-[#808285] font-[500]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {/* Details */}
              {activeTab === "details" && (
                <ActiveSalesDetailsTab mockData={mockData} />
              )}

              {/* Transactions */}
              {activeTab === "transactions" && (
                <ActiveInvestmentTransactionsTab
                  transactions={transactions}
                  loadingTransactions={loadingTransactions}
                  error={transactionsError}
                />
              )}

              {/* Updates */}
              {activeTab === "updates" && (
                <ActiveInvestmentUpdatesTab
                  updates={updates}
                  loadingUpdates={loadingUpdates}
                  error={updatesError}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Early Exit Modals */}
      {mockData && (
        <>
          <RequestEarlyExitModal
            open={activeModal === "request"}
            handleClose={() => setActiveModal(null)}
            mockData={mockData}
            onListClick={handleListOnMarket}
          />

          <EarlyExitPinModal
            open={activeModal === "pin"}
            handleClose={() => setActiveModal(null)}
            onConfirm={handlePinConfirm}
          />

          <PositionListedSuccessModal
            open={activeModal === "success"}
            handleClose={() => setActiveModal(null)}
            mockData={mockData}
          />
        </>
      )}
    </Drawer>
  );
}

export default ActiveSalesInvestmentDrawer;
